import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import * as PhosphorIcons from "@phosphor-icons/react";
import {
  ArrowRight,
  ArrowLeft,
  Phone,
  CheckCircle,
  MapPin,
} from "@phosphor-icons/react";
import Layout from "@/components/site/Layout";
import { services, getServiceBySlug } from "@/data/services";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const Icon = PhosphorIcons[service.icon] || PhosphorIcons.Wrench;
  const currentIdx = services.findIndex((s) => s.slug === service.slug);
  const next = services[(currentIdx + 1) % services.length];
  const prev = services[(currentIdx - 1 + services.length) % services.length];

  return (
    <Layout>
      {/* HERO */}
      <section
        data-testid="service-detail-hero"
        className="relative isolate overflow-hidden border-b border-[#C4C4B5]"
        style={{
          backgroundImage: `linear-gradient(rgba(17,17,16,0.78), rgba(17,17,16,0.5)), url('${service.image}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 md:pt-24 pb-20 md:pb-28 min-h-[60vh] flex flex-col justify-end">
          <Link
            to="/services"
            data-testid="service-detail-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#F4F4EB]/70 hover:text-[#E5231A] mb-8 w-fit"
          >
            <ArrowLeft size={14} weight="bold" /> All Services
          </Link>

          <div className="flex items-center gap-3 text-[#E5231A] mb-5">
            <span className="w-12 h-12 border border-[#E5231A] flex items-center justify-center bg-black/40">
              <Icon size={24} weight="duotone" />
            </span>
            <span className="uppercase text-xs tracking-[0.25em] font-bold">
              Schaefer Services · Abilene, TX
            </span>
          </div>

          <h1
            data-testid="service-detail-heading"
            className="font-display font-black uppercase tracking-tighter text-[#F4F4EB] text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] max-w-4xl"
          >
            {service.name}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#F4F4EB]/80 leading-relaxed">
            {service.short}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to={`/#contact`}
              state={{ service: service.name }}
              data-testid="service-detail-cta-quote"
              className="btn-hard inline-flex items-center justify-center gap-2 bg-[#E5231A] text-[#F4F4EB] font-bold uppercase text-sm tracking-wider px-7 py-4 border border-[#F4F4EB]"
            >
              Request a Quote
              <ArrowRight size={18} weight="bold" />
            </Link>
            <a
              href="tel:+13255133501"
              data-testid="service-detail-cta-call"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-[#F4F4EB] font-bold uppercase text-sm tracking-wider px-7 py-4 border border-[#F4F4EB] hover:bg-[#F4F4EB] hover:text-[#111110] transition-colors"
            >
              <Phone size={18} weight="fill" /> (325) 513-3501
            </a>
          </div>
        </div>
      </section>

      {/* OVERVIEW + INCLUDES */}
      <section
        data-testid="service-detail-body"
        className="bg-[#F4F4EB] py-20 md:py-28 border-b border-[#C4C4B5]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="uppercase text-xs tracking-[0.25em] font-bold text-[#E5231A] mb-4">
              What You Get
            </div>
            <h2 className="font-display font-black uppercase tracking-tight text-[#111110] text-3xl md:text-4xl leading-tight">
              Straightforward work.
              <br />
              Done the right way.
            </h2>
            <div className="mt-6 space-y-5 text-base md:text-lg leading-relaxed text-[#595955] max-w-2xl">
              {service.long.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="border-2 border-[#111110] bg-[#EAEAE0] p-6 md:p-8">
              <div className="uppercase text-xs tracking-[0.25em] font-bold text-[#E5231A] mb-4">
                Typically Includes
              </div>
              <ul className="space-y-3" data-testid="service-detail-includes">
                {service.includes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#111110]">
                    <CheckCircle
                      size={20}
                      weight="fill"
                      className="text-[#E5231A] mt-0.5 shrink-0"
                    />
                    <span className="text-sm md:text-base leading-snug">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-[#C4C4B5]">
                <div className="flex items-center gap-2 text-[#595955] text-sm">
                  <MapPin size={16} weight="fill" className="text-[#E5231A]" />
                  Serving Abilene, TX &amp; Surrounding
                </div>
                <Link
                  to={`/#contact`}
                  state={{ service: service.name }}
                  data-testid="service-detail-sidebar-cta"
                  className="mt-5 btn-hard w-full inline-flex items-center justify-center gap-2 bg-[#E5231A] text-[#F4F4EB] font-bold uppercase text-sm tracking-wider px-6 py-3 border border-[#111110]"
                >
                  Get a Free Quote
                  <ArrowRight size={16} weight="bold" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* PREV / NEXT */}
      <section className="bg-[#EAEAE0] border-b border-[#C4C4B5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 grid sm:grid-cols-2 gap-px bg-[#C4C4B5] border border-[#C4C4B5]">
          <Link
            to={`/services/${prev.slug}`}
            data-testid="service-detail-prev"
            className="group bg-[#EAEAE0] hover:bg-[#111110] p-6 md:p-8 transition-colors"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-[#E5231A] mb-2">
              <ArrowLeft size={12} weight="bold" /> Previous
            </div>
            <div className="font-display font-extrabold uppercase text-xl md:text-2xl text-[#111110] group-hover:text-[#F4F4EB] transition-colors">
              {prev.name}
            </div>
          </Link>
          <Link
            to={`/services/${next.slug}`}
            data-testid="service-detail-next"
            className="group bg-[#EAEAE0] hover:bg-[#111110] p-6 md:p-8 transition-colors sm:text-right"
          >
            <div className="flex items-center sm:justify-end gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-[#E5231A] mb-2">
              Next <ArrowRight size={12} weight="bold" />
            </div>
            <div className="font-display font-extrabold uppercase text-xl md:text-2xl text-[#111110] group-hover:text-[#F4F4EB] transition-colors">
              {next.name}
            </div>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
